/**
 * ShareStore Utility Functions
 *
 * Pure helper functions for file handling, MIME type mapping,
 * filename management, and folder document counting.
 */

import type { ShareFolderNode } from "@/stores/documents/repository/shares";

// ============================================================================
// Folder Counting
// ============================================================================

/**
 * Total number of documents under a share folder, counted recursively —
 * the folder's own documents plus every document nested in its descendants.
 *
 * Shared by the share sidebar tree and the subfolder table rows so both
 * surfaces report the same recursive total (matching the header's API totals).
 * Counting only direct children here is the bug behind the inconsistent
 * folder/doc counts on the share-link page.
 */
export function getTotalDocCount(folder: ShareFolderNode): number {
  return (
    folder.documents.length +
    folder.children.reduce((sum, child) => sum + getTotalDocCount(child), 0)
  );
}

// ============================================================================
// MIME Type Mapping
// ============================================================================

/**
 * Map of common MIME types to file extensions
 */
export const MIME_TO_EXTENSION: Record<string, string> = {
  // Documents
  "application/pdf": ".pdf",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.oasis.opendocument.spreadsheet": ".ods",
  "application/vnd.ms-powerpoint": ".ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",

  // Images
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/webp": ".webp",
  "image/svg+xml": ".svg",

  // CAD/Engineering
  "application/x-dwg": ".dwg",
  "image/vnd.dwg": ".dwg",
  "application/acad": ".dwg",
  "application/dxf": ".dxf",
  "model/vnd.dwf": ".dwf",

  // Text/Code
  "text/plain": ".txt",
  "text/html": ".html",
  "text/css": ".css",
  "text/javascript": ".js",
  "application/json": ".json",
  "application/xml": ".xml",

  // Archives
  "application/zip": ".zip",
};

// ============================================================================
// Filename Utilities
// ============================================================================

/**
 * Ensure filename has proper extension based on content type.
 * If filename already has an extension, returns as-is.
 * Otherwise, appends extension based on MIME type.
 */
export function ensureFileExtension(filename: string, contentType: string | null): string {
  if (!contentType) return filename;

  // Check if filename already has an extension
  const lastDotIndex = filename.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0 && lastDotIndex > filename.lastIndexOf("/");

  if (hasExtension) {
    return filename;
  }

  // Try to get extension from MIME type
  const extension = MIME_TO_EXTENSION[contentType.toLowerCase()];
  if (extension) {
    return filename + extension;
  }

  return filename;
}

/**
 * Generate unique filename to avoid duplicates in zip.
 * Appends (1), (2), etc. if name already exists.
 */
export function makeUniqueFilename(filename: string, existingNames: Set<string>): string {
  if (!existingNames.has(filename)) {
    existingNames.add(filename);
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? filename.substring(0, lastDotIndex) : filename;
  const extension = hasExtension ? filename.substring(lastDotIndex) : "";

  let counter = 1;
  let newName = `${baseName} (${counter})${extension}`;
  while (existingNames.has(newName)) {
    counter++;
    newName = `${baseName} (${counter})${extension}`;
  }

  existingNames.add(newName);
  return newName;
}

/**
 * Sanitize a string for use as a filename.
 * Replaces non-alphanumeric characters with hyphens.
 */
export function sanitizeFilename(name: string): string {
  return name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
}
